import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Typography from "@mui/joy/Typography";
import FormControl from "@mui/joy/FormControl";
import FormHelperText from "@mui/joy/FormHelperText";
import FormLabel from "@mui/joy/FormLabel";
import FormInput from "@mui/joy/Input";
import Tooltip from "@mui/joy/Tooltip";
import { SxProps } from "@mui/joy/styles/types";
import WarningOutlined from "@mui/icons-material/WarningOutlined";
import { Box } from "@mui/system";

interface Props {
  id?: string;
  name: string;
  label?: string;
  register: any;
  step?: string;
  infoText?: string;
  helperText?: string | JSX.Element;
  autoFocus?: boolean;
  autoComplete?: string;
  defaultValue?: string | number;
  type?: "text" | "number" | "email" | "time" | "color" | "password" | "date";
  placeholder?: string;
  error?: string;
  required?: boolean;
  min?: string;
  max?: string;
  maxLength?: number;
  disabled?: boolean;
  readOnly?: boolean;
  sx?: SxProps;
  outerSx?: SxProps;
  endDecorator?: React.ReactNode;
  watch?: any;
  inputStyles?: React.CSSProperties;
  transform?: (value: string) => string;
}

const Input = ({
  name,
  label,
  infoText,
  readOnly,
  autoFocus,
  autoComplete,
  helperText,
  register,
  defaultValue,
  step,
  placeholder,
  error,
  maxLength,
  required,
  disabled,
  sx,
  outerSx,
  endDecorator,
  watch,
  inputStyles,
  type = "text",
  transform,
  ...props
}: Props) => {
  return (
    <FormControl
      key={name}
      sx={[
        {
          width: "100%",
          position: !label && required && "relative",
        },
        ...(Array.isArray(outerSx) ? outerSx : [outerSx]),
      ]}
      size="sm"
      error={!!error}
    >
      {label && (
        <FormLabel
          sx={{
            fontSize: "sm",
            fontWeight: "lg",
            mb: 1,
            width: "100%",
            position: "relative",
          }}
          required={required}
        >
          {infoText && (
            <Tooltip
              title={infoText}
              sx={{
                maxWidth: "250px",
              }}
              arrow
              placement="right"
            >
              <InfoOutlinedIcon
                sx={{
                  fontSize: 15,
                  mr: 0.5,
                }}
              />
            </Tooltip>
          )}

          {label}
          {endDecorator && (
            <Box
              component={"span"}
              sx={{
                position: "absolute",
                display: "flex",
                alignItems: "center",
                right: 0,
              }}
            >
              {endDecorator}
            </Box>
          )}
        </FormLabel>
      )}
      {!label && required && (
        <Typography
          sx={{
            color: "danger.500",
            position: "absolute",
            right: -6,
            top: -12,
            zIndex: 9999,
          }}
        >
          *
        </Typography>
      )}
      <FormInput
        type={type}
        autoComplete={autoComplete}
        placeholder={placeholder}
        error={!!error}
        defaultValue={defaultValue}
        {...register(name, {
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            const transformed = transform ? transform(e.target.value) : e.target.value;
            e.target.value = transformed;
          },
        })}
        disabled={disabled}
        autoFocus={autoFocus}
        readOnly={readOnly}
        sx={[
          {
            py: 1,
            "& :disabled": {
              cursor: "not-allowed !important",
            },
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        endDecorator={
          !label &&
          infoText && (
            <Tooltip
              title={infoText}
              sx={{
                maxWidth: "250px",
              }}
              arrow
              placement="right"
            >
              <InfoOutlinedIcon
                sx={{
                  fontSize: 15,
                }}
              />
            </Tooltip>
          )
        }
        slotProps={{
          input: {
            min: Object.keys(props).includes("min") ? props.min : undefined,
            max: Object.keys(props).includes("max") ? props.max : undefined,
            step: step || undefined,
            onWheel: (e) => {
              if (type === "number") {
                e.currentTarget.blur();
              }
            },
            style: {
              ...inputStyles,
            },
          },
        }}
      />

      {watch && maxLength && watch(name)?.length && watch(name).length > maxLength ? (
        <Typography color="warning" sx={{ mt: 0.6, fontSize: "xs" }} startDecorator={<WarningOutlined />}>
          Ideal Characters Limit Exceeded {watch(name).length - maxLength}
        </Typography>
      ) : (
        false
      )}

      {helperText && <Typography sx={{ mt: 0.6, fontSize: "xs", color: "neutral.700" }}>{helperText}</Typography>}

      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
};
export default Input;
